import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ColumnDef,
} from 'material-react-table';
import { defineMessages, IntlShape, useIntl } from 'react-intl';

import { getObjectTypeLabel } from '../../components/enums/objectTypes';
import {
  getParentTypeColor,
  isParentType,
} from '../../components/enums/parentObjectTypes';
import { formatDate } from '../../helpers';
import { useMaterialReactTableLocalization } from '../../i18n';

interface ObjectRow {
  id: string;
  name?: string | null;
  type?: string | null;
  siteId?: string | null;
  siteStatus: 'none' | 'accessible' | 'restricted';
  groupId?: string | null;
  updatedAt?: string | null;
}

interface SiteOption {
  id: string;
  name: string;
}

interface ObjectGroupOption {
  id: string;
  name?: string | null;
}

const objectTypeMessages = defineMessages({
  fortification: {
    id: 'objects.type.fortification',
    defaultMessage: 'Fortification',
  },
  mound: { id: 'objects.type.mound', defaultMessage: 'Mound' },
  wall: { id: 'objects.type.wall', defaultMessage: 'Wall' },
  ditch: { id: 'objects.type.ditch', defaultMessage: 'Ditch' },
  building: { id: 'objects.type.building', defaultMessage: 'Building' },
  structure: { id: 'objects.type.structure', defaultMessage: 'Structure' },
  boundary: { id: 'objects.type.boundary', defaultMessage: 'Boundary' },
  vegsign: {
    id: 'objects.type.vegsign',
    defaultMessage: 'Vegetation sign',
  },
  viereckschanze: {
    id: 'objects.type.viereckschanze',
    defaultMessage: 'Viereckschanze',
  },
  pit: { id: 'objects.type.pit', defaultMessage: 'Pit' },
  'pit-house': { id: 'objects.type.pitHouse', defaultMessage: 'Pit house' },
  enclosure: { id: 'objects.type.enclosure', defaultMessage: 'Enclosure' },
  grave: { id: 'objects.type.grave', defaultMessage: 'Grave' },
  rondel: { id: 'objects.type.rondel', defaultMessage: 'Rondel' },
  rondeloid: { id: 'objects.type.rondeloid', defaultMessage: 'Rondeloid' },
  deposit_site: {
    id: 'objects.type.depositSite',
    defaultMessage: 'Deposit site',
  },
  other: { id: 'objects.type.other', defaultMessage: 'Other' },
  parent_necropolis: {
    id: 'objects.type.parentNecropolis',
    defaultMessage: 'Necropolis',
  },
  parent_fortification: {
    id: 'objects.type.parentFortification',
    defaultMessage: 'Fortified site',
  },
  parent_hillfort: {
    id: 'objects.type.parentHillfort',
    defaultMessage: 'Hillfort',
  },
  parent_settlement: {
    id: 'objects.type.parentSettlement',
    defaultMessage: 'Settlement',
  },
  parent_polyculture: {
    id: 'objects.type.parentPolyculture',
    defaultMessage: 'Polyculture site',
  },
  parent_oppidum: {
    id: 'objects.type.parentOppidum',
    defaultMessage: 'Oppidum',
  },
  parent_viereckschanze: {
    id: 'objects.type.parentViereckschanze',
    defaultMessage: 'Viereckschanze site',
  },
  parent_deposit_site: {
    id: 'objects.type.parentDepositSite',
    defaultMessage: 'Deposit site',
  },
});

const getLocalizedObjectTypeLabel = (
  type: string | null | undefined,
  intl: IntlShape
) => {
  if (!type) {
    return '-';
  }

  const message = objectTypeMessages[type as keyof typeof objectTypeMessages];

  return message
    ? intl.formatMessage(message)
    : getObjectTypeLabel(type) ?? '-';
};

function createColumns(
  siteNames: Map<string, string>,
  groupNames: Map<string, string>,
  intl: IntlShape
): MRT_ColumnDef<ObjectRow>[] {
  return [
    {
      accessorKey: 'name',
      header: intl.formatMessage({
        id: 'objects.table.name',
        defaultMessage: 'Name',
      }),
      size: 220,
      Cell: ({ renderedCellValue }) => (
        <span>
          {renderedCellValue || (
            <i>
              {intl.formatMessage({
                id: 'objects.table.unnamed',
                defaultMessage: 'Unnamed',
              })}
            </i>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'type',
      header: intl.formatMessage({
        id: 'objects.table.type',
        defaultMessage: 'Type',
      }),
      size: 180,
      Cell: ({ cell }) => {
        const type = cell.getValue<string | null | undefined>();
        const typeLabel = getLocalizedObjectTypeLabel(type, intl);

        if (!isParentType(type)) {
          return typeLabel;
        }

        const tooltip = intl.formatMessage(
          {
            id: 'objects.table.parentType',
            defaultMessage: 'Parent {type}',
          },
          { type: typeLabel }
        );
        const indicatorStyles = {
          ...parentTypeIndicatorStyles,
          bgcolor: getParentTypeColor(type),
        };

        return (
          <Stack component="span" direction="row" alignItems="center" gap={1}>
            <span>{typeLabel}</span>
            <Tooltip arrow title={tooltip}>
              <Box
                component="span"
                role="img"
                aria-label={tooltip}
                sx={indicatorStyles}
              >
                <AccountTreeOutlinedIcon fontSize="inherit" />
              </Box>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      accessorKey: 'siteId',
      header: intl.formatMessage({
        id: 'objects.table.site',
        defaultMessage: 'Site',
      }),
      size: 160,
      Cell: ({ row }) => {
        if (row.original.siteStatus === 'restricted') {
          return intl.formatMessage({
            id: 'map.objects.siteRestricted',
            defaultMessage: 'Linked site not shared with you',
          });
        }

        const siteId = row.original.siteId;

        return siteId ? siteNames.get(siteId) ?? '-' : '-';
      },
    },
    {
      accessorKey: 'groupId',
      header: intl.formatMessage({
        id: 'objects.table.group',
        defaultMessage: 'Group',
      }),
      size: 160,
      Cell: ({ cell }) => {
        const groupId = cell.getValue<string | null>();

        return groupId ? groupNames.get(groupId) ?? '-' : '-';
      },
    },
    {
      accessorFn: (row) => row.updatedAt && new Date(row.updatedAt),
      header: intl.formatMessage({
        id: 'objects.table.lastUpdated',
        defaultMessage: 'Last updated',
      }),
      size: 140,
      Cell: ({ cell }) => formatDate(cell.getValue<Date | null>()) || '-',
    },
  ];
}

const gridStyles = { marginBottom: 6, display: 'grid' };
const parentTypeIndicatorStyles = {
  alignItems: 'center',
  borderRadius: '50%',
  color: 'common.white',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 14,
  height: 24,
  justifyContent: 'center',
  width: 24,
};

interface ObjectsTableProps {
  rows: ObjectRow[];
  sites: SiteOption[];
  groups?: ObjectGroupOption[];
  loading?: boolean;
}

const ObjectsTable = ({
  rows,
  sites,
  groups = [],
  loading = false,
}: ObjectsTableProps) => {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const siteNames = React.useMemo(
    () => new Map(sites.map((site) => [site.id, site.name])),
    [sites]
  );
  const groupNames = React.useMemo(
    () =>
      new Map(
        groups.map((group) => [
          group.id,
          group.name ||
            intl.formatMessage({
              id: 'objects.groups.unnamed',
              defaultMessage: 'Unnamed group',
            }),
        ])
      ),
    [groups, intl]
  );
  const columns = React.useMemo(
    () => createColumns(siteNames, groupNames, intl),
    [groupNames, intl, siteNames]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableToolbarInternalActions: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: 'compact',
    },
    state: { isLoading: loading },
    muiPaginationProps: { showRowsPerPage: false },
    muiTablePaperProps: { sx: gridStyles },
  });

  return <MaterialReactTable table={table} />;
};

export { ObjectsTable };
